import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { FloatingParticles } from './FloatingParticles';
import { useTheme } from './theme-provider';

interface ThreeSceneProps {
  children?: React.ReactNode;
  showParticles?: boolean;
  particleCount?: number;
  particleColor?: string;
  cameraPosition?: [number, number, number];
}

export function ThreeScene({ 
  children, 
  showParticles = true, 
  particleCount = 800,
  particleColor,
  cameraPosition = [0, 0, 5]
}: ThreeSceneProps) {
  const { theme } = useTheme();
  
  // Use theme-aware colors if no specific color is provided
  const defaultParticleColor = theme === 'dark' ? '#6366f1' : '#3b82f6';
  const finalParticleColor = particleColor || defaultParticleColor;

  return (
    <div className="w-full h-full absolute inset-0 pointer-events-none">
      <Canvas
        camera={{ position: cameraPosition, fov: 75 }}
        style={{ 
          background: theme === 'dark'
            ? 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 100%)'
            : 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, transparent 100%)'
        }}
        gl={{ alpha: true, antialias: true }}
      >
        <ambientLight intensity={theme === 'dark' ? 0.5 : 0.7} />
        <pointLight 
          position={[10, 10, 10]} 
          intensity={0.8} 
          color={theme === 'dark' ? '#6366f1' : '#3b82f6'} 
        />
        <pointLight 
          position={[-10, -10, -10]} 
          intensity={0.6} 
          color={theme === 'dark' ? '#8b5cf6' : '#8b5cf6'} 
        />
        
        {showParticles && (
          <FloatingParticles 
            count={particleCount} 
            color={finalParticleColor}
            speed={0.3}
          />
        )}
        
        {children}
        
        <OrbitControls 
          enableZoom={false} 
          enablePan={false} 
          enableRotate={false}
          autoRotate
          autoRotateSpeed={0.5}
        />
      </Canvas>
    </div>
  );
}
